import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import CVDropzone from "@/components/CVDropzone";
import { useLanguage } from "@/context/LanguageContext";
import { publicApi as api, formatApiError } from "@/lib/api";

export default function ApplicationForm({ jobId = null, jobTitle = null }) {
  const { t } = useLanguage();
  const f = t.appForm;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error(f.cvRequired);
    if (!form.name || !form.email) return toast.error(f.nameEmail);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("message", form.message);
      if (jobId) fd.append("job_id", jobId);
      fd.append("cv", file);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success(f.ok);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="application-success" className="border border-y5-yellow bg-y5-surface p-10 text-center">
        <CheckCircle2 className="mx-auto mb-5 text-y5-yellow" size={48} />
        <h3 className="font-display text-3xl mb-3">{f.successTitle}</h3>
        <p className="text-y5-muted max-w-md mx-auto">{f.successText.replace("{name}", form.name)}</p>
      </div>
    );
  }

  const inputCls = "w-full bg-y5-ink border border-y5-line px-4 py-3.5 text-white placeholder:text-white/30 focus:border-y5-yellow focus:outline-none transition-colors";

  return (
    <form onSubmit={submit} data-testid="application-form" className="space-y-5">
      {jobTitle && (
        <div className="text-sm text-y5-muted">{f.role} : <span className="text-y5-yellow font-medium">{jobTitle}</span></div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <input name="name" data-testid="app-name" value={form.name} onChange={onChange} placeholder={f.name} className={inputCls} />
        <input name="email" data-testid="app-email" type="email" value={form.email} onChange={onChange} placeholder={f.email} className={inputCls} />
      </div>
      <input name="phone" data-testid="app-phone" value={form.phone} onChange={onChange} placeholder={f.phone} className={inputCls} />
      <textarea name="message" data-testid="app-message" value={form.message} onChange={onChange} placeholder={f.message} rows={4} className={inputCls} />
      <CVDropzone file={file} setFile={setFile} />
      <button type="submit" data-testid="app-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-y5-yellow text-y5-ink font-semibold py-4 hover:scale-[1.01] transition-transform disabled:opacity-60">
        {loading ? <><Loader2 className="animate-spin" size={18} /> {f.analysing}</> : f.submit}
      </button>
    </form>
  );
}
